import { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { stagger, fadeUp } from '@/lib/animations';
import { useParams, useNavigate } from 'react-router-dom';
import { Building2, MapPin, Edit, FileText, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { DetailLayout, DetailList, RailCard } from '@/components/common/DetailPanels';
import {
  PageHeader,
  DataTable,
  Column,
  StatusBadge,
  MetricCard,
  LoadingState,
  KPIStrip,
} from '@/components/common';
import {
  suppliersApi,
  Supplier,
  SupplierPerformance,
  PurchaseOrder,
  purchaseOrdersApi,
} from '@/api/services';
import { getErrorMessage } from '@/api/client';
import { formatCurrency, formatMoneyTotal } from '@/lib/utils';

const poStatusMap: Record<string, 'draft' | 'pending' | 'approved' | 'processing' | 'completed'> = {
  DRAFT: 'draft',
  SUBMITTED: 'pending',
  APPROVED: 'approved',
  ORDERED: 'processing',
  PARTIALLY_RECEIVED: 'processing',
  RECEIVED: 'completed',
  CANCELLED: 'draft',
};

export default function SupplierDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [supplier, setSupplier] = useState<Supplier | null>(null);
  const [performance, setPerformance] = useState<SupplierPerformance | null>(null);
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrder[]>([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    if (!id) return;
    try {
      setLoading(true);
      const [supplierData, performanceData, posData] = await Promise.all([
        suppliersApi.getById(id),
        suppliersApi.getPerformance(id).catch(() => null),
        purchaseOrdersApi.getAll({ supplierId: id, limit: 10 }),
      ]);
      setSupplier(supplierData);
      setPerformance(performanceData);
      setPurchaseOrders(posData.data);
    } catch (error) {
      toast.error(getErrorMessage(error));
      navigate('/suppliers');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchData();
  }, [id, fetchData]);

  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  /**
   * A local formatter used to hardcode USD, so a supplier billing in dirhams
   * had its orders rendered with a dollar sign. The currency now comes off the
   * record.
   */
  const money = (amount?: number | null, currency?: string) =>
    amount == null ? '-' : formatCurrency(amount, currency || 'USD');

  if (loading) {
    return <LoadingState />;
  }

  if (!supplier) {
    return null;
  }

  const getRatingBadge = (rating?: number) => {
    if (rating === undefined || rating === null) return <Badge variant="outline">No Rating</Badge>;
    const percentage = rating * 20;
    if (percentage >= 90)
      return <Badge className="bg-success-50 text-success-600">Excellent</Badge>;
    if (percentage >= 75) return <Badge className="bg-primary-50 text-primary-500">Good</Badge>;
    if (percentage >= 60) return <Badge className="bg-warning-50 text-warning-600">Average</Badge>;
    return <Badge className="bg-danger-50 text-danger-600">Needs Improvement</Badge>;
  };

  const poColumns: Column<PurchaseOrder>[] = [
    {
      key: 'poNumber',
      header: 'PO Number',
      render: (po) => <span className="font-medium">{po.poNumber}</span>,
    },
    {
      key: 'orderDate',
      header: 'Order Date',
      render: (po) => <span className="text-muted-foreground">{formatDate(po.orderDate)}</span>,
    },
    {
      key: 'totalAmount',
      header: 'Amount',
      render: (po) => <span className="font-medium">{money(po.totalAmount, po.currency)}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (po) => <StatusBadge status={poStatusMap[po.status] || 'draft'} />,
    },
  ];

  return (
    <motion.main className="space-y-5" variants={stagger} initial="hidden" animate="show">
      <motion.div variants={fadeUp}>
        <PageHeader
          backLink={{ label: 'Back to Suppliers', href: '/suppliers' }}
          title={supplier.name}
          description={`Code: ${supplier.code}`}
          actions={
            <Button variant="outline">
              <Edit className="mr-2 h-4 w-4" />
              Edit Supplier
            </Button>
          }
        />
      </motion.div>

      {/* KPI Cards */}
      <div className="mt-6">
        <KPIStrip columns={4}>
          <MetricCard
            label="Total Orders"
            value={performance?.metrics.totalOrders || supplier._count?.purchaseOrders || 0}
            sentiment="info"
            icon={<FileText className="h-5 w-5" />}
          />
          <MetricCard
            label="Total Value"
            value={formatMoneyTotal(
              performance?.metrics.totalValue,
              performance?.metrics.currency,
              performance?.metrics.spendByCurrency
            )}
            sentiment="success"
          />
          <MetricCard
            label="On-Time Delivery"
            value={
              performance?.metrics.onTimeDeliveryRate == null
                ? 'Not measured'
                : `${performance.metrics.onTimeDeliveryRate}%`
            }
            sentiment={
              performance?.metrics.onTimeDeliveryRate &&
              performance.metrics.onTimeDeliveryRate >= 90
                ? 'success'
                : 'warning'
            }
            icon={<TrendingUp className="h-5 w-5" />}
          />
          <MetricCard
            label="Rating"
            value={supplier.rating ? `${supplier.rating}/5` : '-'}
            sentiment={supplier.rating && supplier.rating >= 4 ? 'success' : 'neutral'}
          />
        </KPIStrip>
      </div>

      {/* Performance and order history are what you came to read; who the
          supplier is and how to reach them is reference. Previously the
          reference held the narrow left column and the work the wide right,
          which is the wrong way round. */}
      <motion.div variants={fadeUp} className="mt-6">
        <DetailLayout
          rail={
            <RailCard
              title="Supplier details"
              icon={<Building2 className="h-4 w-4 text-muted-foreground" />}
            >
              <DetailList
                items={[
                  {
                    label: 'Status',
                    value: <StatusBadge status={supplier.isActive ? 'active' : 'inactive'} />,
                  },
                  { label: 'Rating', value: getRatingBadge(supplier.rating) },
                  { label: 'Contact', value: supplier.contactName, when: !!supplier.contactName },
                  {
                    label: 'Email',
                    value: (
                      <a href={`mailto:${supplier.email}`} className="hover:underline">
                        {supplier.email}
                      </a>
                    ),
                    when: !!supplier.email,
                  },
                  { label: 'Phone', value: supplier.phone, when: !!supplier.phone },
                ]}
              />

              {supplier.address && (
                <div className="mt-4 border-t pt-3">
                  <p className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    <MapPin className="h-3 w-3" />
                    Address
                  </p>
                  <div className="text-sm text-muted-foreground">
                    {supplier.address.street && <p>{supplier.address.street}</p>}
                    <p>
                      {[supplier.address.city, supplier.address.state, supplier.address.postalCode]
                        .filter(Boolean)
                        .join(', ')}
                    </p>
                    {supplier.address.country && <p>{supplier.address.country}</p>}
                  </div>
                </div>
              )}
            </RailCard>
          }
          main={
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <h3 className="text-base font-semibold text-foreground">Performance metrics</h3>
                  <CardDescription>Historical performance data</CardDescription>
                </CardHeader>
                <CardContent>
                  {/* Was two whole cards, one per branch of `performance`,
                      differing only in their body. */}
                  {performance ? (
                    <div className="grid grid-cols-2 gap-6 sm:grid-cols-4">
                      <div className="space-y-1">
                        <p className="text-sm text-muted-foreground">On-time delivery</p>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-2xl font-semibold tabular-nums">
                            {performance.metrics.onTimeDeliveryRate == null
                              ? 'Not measured'
                              : `${performance.metrics.onTimeDeliveryRate}%`}
                          </span>
                          {(performance.metrics.onTimeDeliveryRate ?? 0) >= 90 && (
                            <Badge className="bg-success-50 text-success-600">Excellent</Badge>
                          )}
                        </div>
                        {/* The figure used to be 100% for every supplier,
                            because having an expected date counted as arriving
                            on one. Saying what it rests on is how somebody
                            would have noticed. */}
                        <p className="text-xs text-muted-foreground">
                          {performance.metrics.onTimeBasis}
                        </p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm text-muted-foreground">Fulfillment rate</p>
                        <span className="text-2xl font-semibold tabular-nums">
                          {performance.metrics.fulfillmentRate}%
                        </span>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm text-muted-foreground">Total orders</p>
                        <span className="text-2xl font-semibold tabular-nums">
                          {performance.metrics.totalOrders}
                        </span>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm text-muted-foreground">Total value</p>
                        <span className="text-2xl font-semibold tabular-nums">
                          {formatMoneyTotal(
                            performance.metrics.totalValue,
                            performance.metrics.currency,
                            performance.metrics.spendByCurrency
                          )}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <p className="text-muted-foreground">No performance data available yet.</p>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <h3 className="text-base font-semibold text-foreground">
                    Recent purchase orders
                  </h3>
                  <CardDescription>Latest orders from this supplier</CardDescription>
                </CardHeader>
                <CardContent>
                  <DataTable
                    pagination
                    data={purchaseOrders}
                    columns={poColumns}
                    onRowClick={(po) => navigate(`/purchase-orders/${po.id}`)}
                    emptyTitle="No purchase orders"
                    emptyDescription="No orders have been placed with this supplier yet."
                  />
                </CardContent>
              </Card>
            </div>
          }
        />
      </motion.div>
    </motion.main>
  );
}
